import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from 'state/store';
import {
  InsightDrilldownSelectedData,
  InsightDrilldownFilter,
  InsightDrilldownChartData,
} from 'types/store/normalized/insights/insightFilterMetadata';
import { DRILLDOWN_FILTER_KEY_MAP } from 'utils/constants/dashboard';
import { insightFilterMetadataSelectors } from '../insights/insightFilterMetadataSlice';

/* ============================== STATE SETUP =============================== */

interface InsightDrilldownFiltersState {
  filters: InsightDrilldownFilter | undefined;
  chartData: InsightDrilldownChartData | undefined;
}

const initialState: InsightDrilldownFiltersState = {
  filters: undefined,
  chartData: undefined,
};

/* ================================= REDUCER ================================ */
const insightDrilldownFiltersSlice = createSlice({
  name: 'normalized/insightDrilldownFilters',
  initialState,
  reducers: {
    setInsightDrilldownFilters: (
      state,
      action: PayloadAction<InsightDrilldownSelectedData | undefined>
    ) => {
      if (!action.payload) return;

      state.filters = action.payload.filters;
      state.chartData = action.payload.chartData;
    },
    clearInsightDrilldownFilters: (state) => {
      state.filters = undefined;
      state.chartData = undefined;
    },
  },
});

/* =============================== SELECTORS ================================ */
const selectInsightDrilldownFilters = (
  state: RootState
): InsightDrilldownSelectedData | undefined => {
  if (
    state.normalized.insightDrilldownFilters.filters &&
    state.normalized.insightDrilldownFilters.chartData
  ) {
    return {
      filters: state.normalized.insightDrilldownFilters.filters,
      chartData: state.normalized.insightDrilldownFilters.chartData,
    };
  }

  return undefined;
};

const FIELD_TO_ID_MAP: Record<string, string> = {
  business_unit_title: 'business_unit_id',
  program_title: 'program_id',
  category_name: 'category_id',
  team_name: 'team_id',
  user_name: 'user_id',
};

const selectInsightDrilldownFiltersForQuery = (
  state: RootState
): Record<string, string | number | boolean | Array<string | number>> => {
  const drilldownFiltersData = selectInsightDrilldownFilters(state);
  if (!drilldownFiltersData) return {};

  let { filters, chartData } = drilldownFiltersData;

  const idField = FIELD_TO_ID_MAP[filters.fieldName];
  if (idField && idField in chartData) {
    const idValue = chartData[idField];
    const fieldValue = !idValue || idValue === 'null' ? 'Unknown' : idValue;
    filters = { fieldName: idField, fieldValue };
  }

  const filterKey = DRILLDOWN_FILTER_KEY_MAP[filters.fieldName];
  if (!filterKey) return {};

  const filterMetadata = insightFilterMetadataSelectors.selectById(
    state,
    filterKey
  );

  if (!filterMetadata) return {};

  const { fieldValue } = filters;
  let convertedValue: string | number | boolean | Array<string | number>;

  switch (filterMetadata.type) {
    case 'integer':
      convertedValue = Number(fieldValue);
      break;
    case 'boolean':
      convertedValue = Boolean(fieldValue);
      break;
    case 'string':
    default:
      convertedValue = String(fieldValue);
      break;
  }

  return { [filterKey]: convertedValue };
};

/* ================================ EXPORTS ================================= */
export const insightDrilldownFiltersSelectors = {
  selectInsightDrilldownFilters,
  selectInsightDrilldownFiltersForQuery,
};

export const { setInsightDrilldownFilters, clearInsightDrilldownFilters } =
  insightDrilldownFiltersSlice.actions;

export default insightDrilldownFiltersSlice.reducer;
